import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, ClipboardList, Clock, Search, Users, Utensils } from 'lucide-react';
/**
 * Navigation rail of "tools" — horizontal by default, or vertical (stacked
 * full-width rows) when embedded inside a panel.
 * Each item supports a count badge and role gating via `hidden`.
 */
export function MCToolsRail({ items, variant = 'horizontal' }) {
    const visible = items.filter((i) => !i.hidden);
    if (visible.length === 0)
        return null;
    if (variant === 'vertical') {
        return (_jsx("nav", { className: "mc-tools-rail mc-tools-rail-vertical flex flex-col", "aria-label": "Tools", children: visible.map((item, idx) => {
                const content = _jsx(MCToolsRailButton, { item: item, variant: "vertical" });
                const separator = idx > 0 ? (_jsx("span", { "aria-hidden": true, className: "block mx-4", style: { height: 1, background: 'var(--mc-divider-soft)' } })) : null;
                if (item.to) {
                    return (_jsxs(React.Fragment, { children: [separator, _jsx(Link, { to: item.to, className: "contents", children: content })] }, item.id));
                }
                return (_jsxs(React.Fragment, { children: [separator, _jsx("button", { type: "button", onClick: item.onClick, className: "contents", children: content })] }, item.id));
            }) }));
    }
    return (_jsx("nav", { className: "mc-panel flex items-stretch gap-0 py-0 px-0 overflow-x-auto", style: { padding: 0 }, "aria-label": "Tools", children: visible.map((item, idx) => {
            const content = _jsx(MCToolsRailButton, { item: item, variant: "horizontal" });
            const separator = idx > 0 ? (_jsx("span", { "aria-hidden": true, className: "self-stretch my-3", style: { width: 1, background: 'var(--mc-divider)' } })) : null;
            if (item.to) {
                return (_jsxs(React.Fragment, { children: [separator, _jsx(Link, { to: item.to, className: "contents", children: content })] }, item.id));
            }
            return (_jsxs(React.Fragment, { children: [separator, _jsx("button", { type: "button", onClick: item.onClick, className: "contents", children: content })] }, item.id));
        }) }));
}
function MCToolsRailButton({ item, variant = 'horizontal', }) {
    const isVertical = variant === 'vertical';
    return (_jsxs("span", { className: `mc-tools-rail-button ${isVertical ? 'w-full' : 'flex-1 min-w-[120px]'} flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${item.active ? 'bg-[color:var(--mc-surface-raised)]' : 'hover:bg-[color:var(--mc-surface-raised)]'}`, children: [_jsx("span", { className: "inline-flex items-center justify-center w-7 h-7 rounded-sm flex-shrink-0", style: {
                    background: item.active ? 'rgba(29, 224, 197, 0.12)' : 'transparent',
                    color: item.active ? 'var(--mc-cyan)' : 'var(--mc-text-label)',
                }, "aria-hidden": true, children: item.icon }), _jsxs("span", { className: "flex-1 flex items-center justify-between gap-2 min-w-0", children: [_jsx("span", { className: "mc-tools-rail-label text-[13px] font-semibold truncate", style: {
                            color: item.active ? 'var(--mc-text-hero)' : 'var(--mc-text-primary)',
                        }, children: item.label }), typeof item.badge === 'number' && item.badge > 0 && (_jsx("span", { className: "mc-mono text-[11px] tabular-nums px-1.5 py-0.5 rounded flex-shrink-0", style: {
                            color: item.alert ? 'var(--mc-text-accent-pink)' : 'var(--mc-text-accent-teal)',
                            background: item.alert
                                ? 'rgba(255, 92, 170, 0.12)'
                                : 'rgba(29, 224, 197, 0.12)',
                        }, children: item.badge }))] })] }));
}
/** Re-export commonly-used icons so callers don't need to import lucide separately. */
export const MCToolsRailIcons = {
    Camera,
    ClipboardList,
    Clock,
    Search,
    Users,
    Utensils,
};
